// packages/web-ui/src/AppShell/TabBar/TabBarItem.tsx
//
// W2-S4 AC #2 — single tab in the `<TabBar>` `role="tablist"`. Pinned tabs
// hoist to the leftmost group (sorting handled by the slice's `tabOrder`),
// suppress the close `×`, and render a 📌 icon. Dirty tabs render a leading
// `•` U+2022. Active tab gets a 2 px taupe underline + `font-semibold`.
//
// Source of truth: AC #2 of story 2-4 + UX §2.2.

import { forwardRef, useCallback, type CSSProperties, type ForwardedRef } from 'react';
import type { JSX, MouseEvent } from 'react';
import {
  FolderOpen,
  GitBranch,
  GitCompare,
  LayoutGrid,
  MessageSquare,
  Network,
  Pin,
  Settings,
  X,
  Zap,
} from 'lucide-react';
import type { ActiveDestination, TabState } from '../../state/navigation.js';

export interface TabBarItemProps {
  tab: TabState;
  isActive: boolean;
  /** Index of this tab in the focus ring (used by the parent's focus management). */
  focusIndex: number;
  /** Whether this tab currently has the keyboard focus within the tablist. */
  isFocused: boolean;
  onActivate: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>, tabId: string) => void;
  onMouseDown?: (event: MouseEvent<HTMLElement>, tabId: string) => void;
  /** dnd-kit listeners + transform for sortable drag (forwarded by `<TabBar>`). */
  dragHandleProps?: {
    style?: CSSProperties;
    listeners?: Record<string, (event: unknown) => void>;
    attributes?: Record<string, unknown>;
  };
  /** Padding applied via inline style — primarily for tests asserting min-h-8. */
  className?: string;
}

const DESTINATION_ICONS: Record<ActiveDestination, typeof MessageSquare> = {
  chat: MessageSquare,
  kb: Network,
  files: FolderOpen,
  views: LayoutGrid,
  diff: GitCompare,
  git: GitBranch,
  settings: Settings,
  skills: Zap,
};

function TabBarItemInner(
  props: TabBarItemProps,
  ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
  const {
    tab,
    isActive,
    isFocused,
    onActivate,
    onClose,
    onContextMenu,
    onMouseDown,
    dragHandleProps,
    className = '',
  } = props;

  const handleClick = useCallback(() => {
    onActivate(tab.id);
  }, [onActivate, tab.id]);

  const handleAuxClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // Middle-click closes the tab (UX §2.2 "Close" row).
      if (event.button === 1) {
        event.preventDefault();
        if (!tab.pinned) onClose(tab.id);
      }
    },
    [onClose, tab.id, tab.pinned],
  );

  const handleCloseClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onClose(tab.id);
    },
    [onClose, tab.id],
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onContextMenu?.(event, tab.id);
    },
    [onContextMenu, tab.id],
  );

  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onMouseDown?.(event, tab.id);
    },
    [onMouseDown, tab.id],
  );

  const Icon = tab.pinned ? Pin : DESTINATION_ICONS[tab.destination];

  // Audi pass: uppercase tracked label, Audi-red 2 px bottom-border on
  // active (via `--accent-taupe` alias), white surface on active vs.
  // panel-grey on inactive. Focus outline switches to near-black for
  // higher optical contrast against the brand-red active stripe.
  const baseClasses =
    'group relative inline-flex items-center gap-1.5 select-none border-b-2 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors';
  const activeClasses = isActive
    ? 'border-b-[var(--accent-taupe)] bg-[var(--bg-canvas)] text-fg'
    : 'border-b-transparent bg-[var(--bg-panel)] text-taupe-700 hover:bg-[var(--bg-panel-hover)] hover:text-fg';
  const focusClasses =
    'focus-visible:outline-2 focus-visible:outline-taupe-900 focus-visible:outline-offset-2';

  const labelMaxWidth = tab.pinned ? '80px' : '200px';

  const style: CSSProperties = {
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
    paddingBottom: 6,
    minHeight: 32,
    maxWidth: tab.pinned ? '64px' : undefined,
    ...dragHandleProps?.style,
  };

  const tooltipPrefix = tab.pinned ? 'Pinned · ' : '';
  const titleText = `${tooltipPrefix}${tab.label}`;

  return (
    <button
      type="button"
      ref={ref}
      role="tab"
      id={`tab-${tab.id}`}
      data-testid={`tab-${tab.id}`}
      data-tab-id={tab.id}
      data-active={isActive ? 'true' : 'false'}
      data-pinned={tab.pinned ? 'true' : 'false'}
      data-dirty={tab.isDirty ? 'true' : 'false'}
      data-focused={isFocused ? 'true' : 'false'}
      aria-selected={isActive}
      aria-controls={`panel-${tab.id}`}
      tabIndex={isActive ? 0 : -1}
      title={titleText}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      style={style}
      className={`${baseClasses} ${activeClasses} ${focusClasses} ${className}`}
      {...(dragHandleProps?.attributes ?? {})}
      {...(dragHandleProps?.listeners ?? {})}
    >
      {tab.isDirty ? (
        <span
          aria-hidden="true"
          data-testid="tab-dirty-dot"
          className="text-[var(--accent-taupe)]"
          style={{ marginRight: 4 }}
        >
          •
        </span>
      ) : null}
      <Icon
        aria-hidden="true"
        size={tab.pinned ? 12 : 14}
        data-testid={tab.pinned ? 'tab-pin-icon' : `tab-icon-${tab.destination}`}
      />
      <span
        className="truncate"
        style={{ maxWidth: labelMaxWidth }}
        data-testid="tab-label"
      >
        {tab.label}
      </span>
      {!tab.pinned ? (
        <span
          role="button"
          aria-label={`Close ${tab.label}`}
          data-testid="tab-close"
          tabIndex={-1}
          onClick={handleCloseClick}
          className={`opacity-0 group-hover:opacity-100 ${
            isActive ? 'opacity-100' : ''
          } hover:bg-[var(--bg-panel-hover)] hover:text-audi-red inline-flex items-center justify-center rounded-none`}
          style={{ width: 16, height: 16, marginLeft: 2 }}
        >
          <X size={14} aria-hidden="true" />
        </span>
      ) : null}
    </button>
  );
}

export const TabBarItem = forwardRef<HTMLButtonElement, TabBarItemProps>(TabBarItemInner);
TabBarItem.displayName = 'TabBarItem';
